import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";

const AddDocument = () => {
  const initialStage = {
    vfcName: "",
    rangeId: "",
    documentName: "",
    documentType: "",
    documentNo: "",
    date: "",
    details: "",
    uploadDocument: "",
  };

  const [errors, setErrors] = useState({});
  const [rangeOptions, setRangeOptions] = useState([]);
  const [vfcData, setVfcData] = useState([]); // Holds VFC data after range selection
  const [formData, setFormData] = useState(initialStage);
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  // Validation function
  const validateForm = (formData) => {
    const newErrors = {};
    if (!formData.vfcName) newErrors.vfcName = "VFC name is required";
    if (!formData.rangeId) newErrors.rangeId = "Please select Range Id";
    if (!formData.documentName)
      newErrors.documentName = "Please enter Document Name";
    if (!formData.documentType)
      newErrors.documentType = "Please select Document Type";
    if (!formData.documentNo) newErrors.documentNo = "Please enter Document No";
    if (!formData.date) newErrors.date = "Please enter Date";
    if (!formData.details) newErrors.details = "Please enter Details";
    if (!formData.uploadDocument)
      newErrors.uploadDocument = "Please enter Upload Document";
    return newErrors;
  };

  useEffect(() => {
    setLoading(true);
    // Fetch range options
    const fetchRange = async () => {
      try {
        const Config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, Config);
        const data = response.data;

        if (Array.isArray(data)) {
          setRangeOptions(data);
        } else {
          console.error("API did not return an array", data);
        }
      } catch (err) {
        console.error("Error fetching ranges", err);
      }
      setLoading(false);
    };

    fetchRange();
  }, [Api]);

  // Fetch VFCs when range is selected
  const fetchVfcByRange = async (rangeId) => {
    setLoading(true);
    try {
      const Config = Authorization();
      const response = await axios.post(
        `${Api}/api/vfcs/vfcs-by-range`,
        {
          rangeId,
        },
        Config
      );
      const data = response.data.vfcs;

      if (Array.isArray(data)) {
        setVfcData(data);
      } else {
        console.error("API did not return an array", data);
      }
    } catch (err) {
      console.error("Error fetching VFCs by range", err);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { id, value, files } = e.target;

    if (id === "rangeId") {
      fetchVfcByRange(value); // Fetch VFCs based on range selection
    }

    setFormData({
      ...formData,
      [id]: files ? files[0] : value, // If file input, set the first file
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all Details");
      return;
    }

    // Prepare FormData for file upload
    const formDataToSend = new FormData();
    console.log("formDataToSend", formDataToSend);
    formDataToSend.append("vfcId", formData.vfcName);
    formDataToSend.append("rangeId", formData.rangeId);
    formDataToSend.append("documentName", formData.documentName);
    formDataToSend.append("documentType", formData.documentType);
    formDataToSend.append("documentNo", formData.documentNo);
    formDataToSend.append("date", formData.date);
    formDataToSend.append("details", formData.details);
    if (formData.uploadDocument) {
      formDataToSend.append("uploadDocument", formData.uploadDocument); // Append file
    }

    try {
      const Config = Authorization();
      const response = await fetch(`${Api}/api/documents/documents`, {
        method: "POST",
        headers: {
          ...Config.headers, // Spread headers from Authorization()
        },
        body: formDataToSend, // Send FormData
      });
      if (response.ok) {
        toast.success("Added Successfully");
        setFormData(initialStage); // Reset the form after successful submission
        setErrors({}); // Clear errors after successful submission
      } else {
        toast.error("Error submitting form");
      }
    } catch (error) {
      toast.error("Error submitting form");
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title"> Add New Documents </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Add New Documents
            </li>
          </ol>
        </nav>
      </div>

      <Container>
        <div className="col-md-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <p className="card-description">New Document Adding Form</p>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="rangeId">
                      <Form.Label>Range</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.rangeId}
                          onChange={handleChange}
                          isInvalid={!!errors.rangeId}
                        >
                          <option value="">Select Range</option>
                          {rangeOptions.map((range) => (
                            <option key={range._id} value={range._id}>
                              {range.rangeCode}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.rangeId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="vfcName">
                      <Form.Label>VFC Name</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.vfcName}
                          onChange={handleChange}
                          isInvalid={!!errors.vfcName}
                        >
                          <option value="">Select VFC</option>
                          {vfcData.map((vfc) => (
                            <option key={vfc._id} value={vfc._id}>
                              {vfc.vfcName}
                            </option>
                          ))}
                        </Form.Control>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.vfcName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="documentName">
                      <Form.Label>Document Name</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          placeholder="Enter Document Name"
                          value={formData.documentName}
                          onChange={handleChange}
                          isInvalid={!!errors.documentName}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.location}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="documentType">
                      <Form.Label>Document Type</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={formData.address}
                          onChange={handleChange}
                          isInvalid={!!errors.address}
                        >
                          <option value="">Select Document Type</option>
                          <option value="renewal">
                            VFC Renewal Certificate
                          </option>
                          <option value="bylaw">VFC Bye Law</option>
                          <option value="audit">VFC Audit Reports</option>
                          <option value="success">Success Story Reports</option>
                        </Form.Control>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.address}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="documentNo">
                      <Form.Label>Document No</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          placeholder="Enter Document No"
                          value={formData.village}
                          onChange={handleChange}
                          isInvalid={!!errors.village}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.village}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="date">
                      <Form.Label>Date</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="date"
                          placeholder="Enter Date"
                          onChange={handleChange}
                          isInvalid={!!errors.panchayath}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.panchayath}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="details">
                      <Form.Label>Details</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          placeholder="Enter Details"
                          value={formData.thaluk}
                          onChange={handleChange}
                          isInvalid={!!errors.thaluk}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.thaluk}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="uploadDocument">
                      <Form.Label>Upload Document</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="file"
                          onChange={handleChange}
                          isInvalid={!!errors.uploadDocument}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.uploadDocument}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Button className="Add_submit" variant="primary" type="submit">
                  Submit
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AddDocument;
